import { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects, useCreateProject } from '../hooks/useProjects';
import { useTeamMembers } from '../hooks/useTeamMembers';
import { useAllTasks } from '../hooks/useTasks';
import { useAllDependencies } from '../hooks/useDependencies';
import { useAllMilestones } from '../hooks/useMilestones';
import { useSprints } from '../hooks/useSprints';
import { QuickStats } from '../components/features/dashboard/QuickStats';
import { ProjectList } from '../components/features/dashboard/ProjectList';
import { ProjectForm } from '../components/features/projects/ProjectForm';
import { ExportButton } from '../components/features/export/ExportButton';
import { Alert } from '../components/ui/Alert';
import { getCurrentUser } from '../lib/supabase';
import type { Database } from '../types/database.types';
import type { ProjectFormData } from '../utils/validation';

type TeamMember = Database['public']['Tables']['team_members']['Row'];

export type SortOption = 'risk' | 'name' | 'committed_date' | 'progress';

/**
 * Dashboard/Overview Page
 * Displays all projects with quick stats, filtering, and sorting
 * 
 * Story: 2.1 - Dashboard/Overview
 * Wireframe: Section 1 - Dashboard/Overview
 */
export const DashboardPage = () => {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<SortOption>('risk');
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Fetch projects and team members
  const { data: projects, isLoading: isLoadingProjects, error: projectsError } = useProjects();
  const { data: teamMembers, isLoading: isLoadingTeamMembers } = useTeamMembers() as { data: TeamMember[] | undefined; isLoading: boolean };

  // Export data — only fetched when projects are loaded
  const projectIds = useMemo(() => (projects ?? []).map(p => p.id), [projects]);
  const { data: allTasks } = useAllTasks(projectIds);
  const { data: allDependencies } = useAllDependencies(projectIds);
  const { data: allMilestones } = useAllMilestones(projectIds);
  const sprints = useSprints();

  // Create project mutation
  const createProject = useCreateProject();

  // Calculate quick stats
  const stats = useMemo(() => {
    if (!projects) {
      return {
        activeProjects: 0,
        atRiskProjects: 0,
        completedProjects: 0,
        totalTeamMembers: teamMembers?.length || 0,
      };
    }

    // Active Projects: Total number of projects (all are active until we have task completion data)
    // At Risk Projects: Projects with at_risk or overdue status
    // Completed Projects: 0 for now (requires task completion tracking - future story)
    // Total Team Members: Count of all team members
    return {
      activeProjects: projects.length,
      atRiskProjects: projects.filter(p => p.risk_status === 'at_risk' || p.risk_status === 'overdue').length,
      completedProjects: 0, // TODO: Calculate from task completion once tasks are integrated
      totalTeamMembers: teamMembers?.length || 0,
    };
  }, [projects, teamMembers]);

  // Sort projects
  const sortedProjects = useMemo(() => {
    if (!projects) return [];

    const sorted = [...projects];

    switch (sortBy) {
      case 'risk':
        // Order: overdue (most critical), at_risk, on_track
        return sorted.sort((a, b) => {
          const riskOrder: Record<string, number> = { overdue: 0, at_risk: 1, on_track: 2 };
          return (riskOrder[a.risk_status] ?? 3) - (riskOrder[b.risk_status] ?? 3);
        });

      case 'name':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));

      case 'committed_date':
        return sorted.sort((a, b) => {
          const aTime = new Date(a.committed_delivery_date).getTime();
          const bTime = new Date(b.committed_delivery_date).getTime();
          if (isNaN(aTime) && isNaN(bTime)) return 0;
          if (isNaN(aTime)) return 1;
          if (isNaN(bTime)) return -1;
          return aTime - bTime;
        });

      case 'progress':
        // TODO: Sort by actual task completion % once task data is integrated (story 3.x).
        // Currently using risk_status as a proxy (on_track ≈ higher progress).
        return sorted.sort((a, b) => {
          const progressOrder: Record<string, number> = { on_track: 0, at_risk: 1, overdue: 2 };
          return (progressOrder[a.risk_status] ?? 3) - (progressOrder[b.risk_status] ?? 3);
        });

      default:
        return sorted;
    }
  }, [projects, sortBy]);

  // Handle project click - navigate to detail view
  const handleProjectClick = (projectId: number) => {
    navigate(`/projects/${projectId}`);
  };

  // Handle sort change
  const handleSortChange = (newSort: SortOption) => {
    setSortBy(newSort);
  };

  // Handle create project form submission
  const handleCreateProject = useCallback(async (formData: ProjectFormData) => {
    try {
      const user = await getCurrentUser();
      if (!user?.id) {
        throw new Error('User must be authenticated to create a project');
      }

      await createProject.mutateAsync({
        name: formData.name,
        committed_delivery_date: formData.committed_delivery_date,
        user_id: user.id,
      });

      setIsFormOpen(false);
    } catch (err) {
      console.error('Project creation error:', err);
      throw err; // re-throw so the form receives the error via React Query or caller
    }
  }, [createProject]);

  // Error state
  if (projectsError) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Alert variant="error">
            <strong>Error loading projects:</strong>
            <br />
            {projectsError instanceof Error ? projectsError.message : 'Unknown error'}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Page Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Dashboard</h1>
            <p className="mt-2 text-lg text-gray-600">
              Overview of all your projects and team performance
            </p>
          </div>
          <div className="flex items-center gap-3">
            <ExportButton
              projects={projects ?? []}
              tasks={allTasks ?? []}
              teamMembers={teamMembers ?? []}
              dependencies={allDependencies ?? []}
              milestones={allMilestones}
              sprints={sprints}
            />
            <button
              onClick={() => navigate('/team-members')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors shadow-sm border border-gray-300"
              aria-label="Manage team members"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Team Members
            </button>
            <button
              onClick={() => navigate('/team-members/timeline')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors shadow-sm"
              aria-label="View team member timelines"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              Team Timelines
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <QuickStats
          activeProjects={stats.activeProjects}
          atRiskProjects={stats.atRiskProjects}
          completedProjects={stats.completedProjects}
          totalTeamMembers={stats.totalTeamMembers}
        />

        {/* Project List */}
        <ProjectList
          projects={sortedProjects}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          onProjectClick={handleProjectClick}
          onCreateClick={() => setIsFormOpen(true)}
          isLoading={isLoadingProjects || isLoadingTeamMembers}
        />

        {/* Create Project Form Modal */}
        <ProjectForm
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleCreateProject}
          isLoading={createProject.isPending}
          error={createProject.error instanceof Error ? createProject.error.message : null}
        />
      </div>
    </div>
  );
};

